import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Eye, Heart, Star, MessageSquare, ShoppingBag, TrendingUp,
  ArrowLeft, BarChart3, Store, Plus, ShieldCheck, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_NAMES = {
  riga: 'Rīga', liepaja: 'Liepāja', ventspils: 'Ventspils', daugavpils: 'Daugavpils',
  jelgava: 'Jelgava', valmiera: 'Valmiera', sigulda: 'Sigulda', cesis: 'Cēsis',
  talsi: 'Talsi', kuldiga: 'Kuldīga', saldus: 'Saldus', bauska: 'Bauska',
  ogre: 'Ogre', madona: 'Madona', jurmala: 'Jūrmala', rezekne: 'Rēzekne',
};

const CATEGORY_COLORS = {
  shop: 'bg-[#D97736]', service: 'bg-[#4A6B53]', event: 'bg-[#6366f1]', project: 'bg-[#8b5cf6]'
};

function StarDisplay({ rating, size = 'w-4 h-4' }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`${size} ${s <= Math.round(rating) ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
      ))}
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchDashboard();
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const { data } = await axios.get(`${API}/analytics/dashboard`);
      setData(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, listings, recent_reviews, shop_profile } = data;
  const maxViews = Math.max(...listings.map(l => l.views), 1);

  const statCards = [
    { label: language === 'lv' ? 'Skatījumi' : 'Views', value: summary.total_views, icon: Eye, color: 'text-[#D97736]', bg: 'bg-[#D97736]/10' },
    { label: language === 'lv' ? 'Izlase' : 'Favorites', value: summary.total_favorites, icon: Heart, color: 'text-red-500', bg: 'bg-red-50' },
    { label: language === 'lv' ? 'Atsauksmes' : 'Reviews', value: summary.total_reviews, icon: Star, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: language === 'lv' ? 'Ziņas' : 'Messages', value: summary.total_messages, icon: MessageSquare, color: 'text-[#4A6B53]', bg: 'bg-[#4A6B53]/10', badge: summary.unread_messages > 0 ? summary.unread_messages : null },
    { label: language === 'lv' ? 'Sludinājumi' : 'Listings', value: summary.total_listings, sub: `${summary.active_listings} ${language === 'lv' ? 'aktīvi' : 'active'}`, icon: ShoppingBag, color: 'text-[#6366f1]', bg: 'bg-[#6366f1]/10' },
    { label: language === 'lv' ? 'Vērtējums' : 'Rating', value: summary.avg_rating || '—', icon: TrendingUp, color: 'text-[#8b5cf6]', bg: 'bg-[#8b5cf6]/10' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="analytics-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[#5C5C5C] hover:text-[#121212]" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-outfit text-3xl font-bold text-[#121212] flex items-center gap-2">
                <BarChart3 className="w-8 h-8 text-[#D97736]" />
                {language === 'lv' ? 'Analītika' : 'Analytics'}
              </h1>
              {shop_profile && (
                <p className="text-[#5C5C5C] flex items-center gap-2 mt-1">
                  <Store className="w-4 h-4" />
                  {shop_profile.shop_name}
                  {shop_profile.is_verified && <ShieldCheck className="w-4 h-4 text-green-600" />}
                </p>
              )}
            </div>
          </div>
          <Link to="/create-listing">
            <Button className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="new-listing-btn">
              <Plus className="w-4 h-4 mr-2" />
              {language === 'lv' ? 'Jauns sludinājums' : 'New Listing'}
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div key={i} className="bg-white border border-[#E5E4E0] rounded-2xl p-5 relative" data-testid={`stat-${i}`}>
                <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div className="text-2xl font-bold text-[#121212] font-outfit">{card.value}</div>
                <div className="text-xs text-[#5C5C5C] mt-1">{card.label}</div>
                {card.sub && <div className="text-xs text-[#5C5C5C]">{card.sub}</div>}
                {card.badge && (
                  <span className="absolute top-3 right-3 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {card.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Listings Performance */}
          <div className="lg:col-span-2 bg-white border border-[#E5E4E0] rounded-2xl p-6">
            <h2 className="font-outfit font-semibold text-lg text-[#121212] mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#D97736]" />
              {language === 'lv' ? 'Sludinājumu veiktspēja' : 'Listing Performance'}
            </h2>

            {listings.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-[#5C5C5C] opacity-30" />
                <p className="text-[#5C5C5C]">{language === 'lv' ? 'Nav sludinājumu' : 'No listings yet'}</p>
                <Link to="/create-listing">
                  <Button className="mt-4 bg-[#D97736] hover:bg-[#C06528] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'lv' ? 'Izveidot pirmo' : 'Create first'}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {listings.map((listing) => (
                  <div key={listing.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#F3F2EE] transition-colors" data-testid={`listing-stat-${listing.id}`}>
                    {/* Thumbnail */}
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#F3F2EE] flex-shrink-0">
                      {listing.image ? (
                        <img src={listing.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-6 h-6 text-[#5C5C5C] opacity-30" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link
                          to={`/listing/${listing.slug || listing.id}`}
                          className="font-medium text-[#121212] truncate hover:text-[#D97736] transition-colors text-sm"
                        >
                          {listing.title}
                        </Link>
                        <Badge className={`${CATEGORY_COLORS[listing.category]} text-white border-0 text-[10px] px-1.5 py-0`}>
                          {listing.category}
                        </Badge>
                        {!listing.is_active && (
                          <Badge variant="outline" className="text-red-500 border-red-300 text-[10px] px-1.5 py-0">
                            {language === 'lv' ? 'Neaktīvs' : 'Inactive'}
                          </Badge>
                        )}
                      </div>

                      {/* Progress bar for views */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Progress value={(listing.views / maxViews) * 100} className="h-1.5 bg-[#F3F2EE]" />
                        </div>
                        <span className="text-xs text-[#5C5C5C] w-12 text-right">{listing.views}</span>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="flex items-center gap-4 flex-shrink-0 text-xs text-[#5C5C5C]">
                      <div className="flex items-center gap-1" title={language === 'lv' ? 'Izlase' : 'Favorites'}>
                        <Heart className="w-3.5 h-3.5 text-red-400" />
                        <span>{listing.favorites}</span>
                      </div>
                      <div className="flex items-center gap-1" title={language === 'lv' ? 'Atsauksmes' : 'Reviews'}>
                        <Star className="w-3.5 h-3.5 text-amber-400" />
                        <span>{listing.reviews}</span>
                      </div>
                      <div className="flex items-center gap-1" title={language === 'lv' ? 'Ziņas' : 'Messages'}>
                        <Mail className="w-3.5 h-3.5 text-[#4A6B53]" />
                        <span>{listing.messages}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Rating Overview */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-[#121212] mb-4">
                {language === 'lv' ? 'Kopējais vērtējums' : 'Overall Rating'}
              </h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-4xl font-bold text-[#D97736] font-outfit">
                  {summary.avg_rating || '—'}
                </div>
                <div>
                  <StarDisplay rating={summary.avg_rating || 0} size="w-5 h-5" />
                  <p className="text-sm text-[#5C5C5C] mt-1">
                    {summary.total_reviews} {language === 'lv' ? 'atsauksmes' : 'reviews'}
                  </p>
                </div>
              </div>
            </div>

            {/* Recent Reviews */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-[#121212] mb-4">
                {language === 'lv' ? 'Jaunākās atsauksmes' : 'Recent Reviews'}
              </h3>
              {recent_reviews.length === 0 ? (
                <p className="text-sm text-[#5C5C5C] text-center py-4">
                  {language === 'lv' ? 'Vēl nav atsauksmju' : 'No reviews yet'}
                </p>
              ) : (
                <div className="space-y-4">
                  {recent_reviews.map(review => (
                    <div key={review.id} className="pb-4 border-b border-[#E5E4E0] last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 rounded-full bg-[#4A6B53] flex items-center justify-center text-white text-xs font-semibold">
                          {review.user_name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-[#121212]">{review.user_name}</span>
                        <StarDisplay rating={review.rating} size="w-3 h-3" />
                      </div>
                      <p className="text-sm text-[#5C5C5C] line-clamp-2 ml-9">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-[#121212] mb-4">
                {language === 'lv' ? 'Ātrās darbības' : 'Quick Actions'}
              </h3>
              <div className="space-y-2">
                <Link to="/create-listing" className="block">
                  <Button variant="outline" className="w-full justify-start border-[#E5E4E0] text-[#5C5C5C] hover:text-[#D97736] hover:border-[#D97736]">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'lv' ? 'Jauns sludinājums' : 'New Listing'}
                  </Button>
                </Link>
                <Link to="/my-listings" className="block">
                  <Button variant="outline" className="w-full justify-start border-[#E5E4E0] text-[#5C5C5C] hover:text-[#D97736] hover:border-[#D97736]">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {language === 'lv' ? 'Mani sludinājumi' : 'My Listings'}
                  </Button>
                </Link>
                <Link to="/messages" className="block">
                  <Button variant="outline" className="w-full justify-start border-[#E5E4E0] text-[#5C5C5C] hover:text-[#D97736] hover:border-[#D97736]">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {language === 'lv' ? 'Ziņas' : 'Messages'}
                    {summary.unread_messages > 0 && (
                      <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {summary.unread_messages}
                      </span>
                    )}
                  </Button>
                </Link>
                <Link to="/shop-profile" className="block">
                  <Button variant="outline" className="w-full justify-start border-[#E5E4E0] text-[#5C5C5C] hover:text-[#D97736] hover:border-[#D97736]">
                    <Store className="w-4 h-4 mr-2" />
                    {language === 'lv' ? 'Veikala profils' : 'Shop Profile'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
